import { FilterDescriptor, SortDescriptor } from '@progress/kendo-data-query';
import { getISODateString } from '@utils-global/date-time';
import { ClaimCategory } from '@module-claims/enums/enum-claim-type';
import {
  CEClaimGroup,
  CEClaimBusinessType
} from '@module-covered-entity/enums/ce-claim.enum';

export interface CoveredEntityClaimPharmacy {
  _id?: string | null;
  account_id?: string | null;
  account_type?: string | null;
  account_name?: string | null;
  file_id?: string | null;
  claim_class_type?: string | null;
  claim_business_type?: CEClaimBusinessType | null;
  ce_id?: string | null;
  ce_site_id?: string | null;
  unique_claim_id?: string | null;
  claim_status?: string | null;
  claim_type?: string | null;
  prescribed_date?: string | null;
  dispense_date?: string | null;
  ship_to_date?: string | null;
  claim_processed_date?: string | null;
  ndc?: string | null;
  drug_name?: string | null;
  prescription_number?: string | null;
  fill_number?: string | null;
  compound_indicator?: number | null;
  product_serilization_number?: string | null;
  quantity_dispensed_unit?: number | null;
  quantity_replenished_unit?: number | null;
  pharmacy_npi?: string | null;
  ship_to_pharmacy_npi?: string | null;
  prescriber_npi?: string | null;
  prescriber_first_name?: string | null;
  prescriber_last_name?: string | null;
  referral_based_claim?: string | null;
  medicaid_id?: string | null;
  patient_mrn_number?: string | null;
  patient_first_name?: string | null;
  patient_middle_name?: string | null;
  patient_last_name?: string | null;
  patient_name_prefix?: string | null;
  patient_name_suffix?: string | null;
  patient_dob?: string | null;
  patient_gender?: string | null;
  primary_bin?: string | null;
  primary_pcn?: string | null;
  primary_group?: string | null;
  secondary_bin?: string | null;
  secondary_pcn?: string | null;
  secondary_group?: string | null;
  tertiary_bin?: string | null;
  tertiary_pcn?: string | null;
  tertiary_group?: string | null;
  wholesaler_account_number?: string | null;
  wholesaler_invoice_number?: string | null;
  wholesaler_invoice_date?: string | null;
  fallout_reason?: string | null;
  ce_340b_identification?: number | null;
  ce_340b_claimed?: number | null;
  rxp_patient_check?: number | null;
  rxp_drug_check?: number | null;
  rxp_payer_check?: number | null;
  rxp_340b_decision?: number | null;
  rxp_claim_certification?: number | null;
  rxp_reason_text?: string | null;
  rxp_codes?: string[] | null;
  rxp_processing_flag?: number | null;
  _row_created_date?: string | null;
  _row_updated_date?: string | null;
}

export interface CoveredEntityClaimMedical {
  _id?: string | null;
  account_id?: string | null;
  account_type?: string | null;
  account_name?: string | null;
  file_id?: string | null;
  claim_class_type?: string | null;
  claim_business_type?: CEClaimBusinessType | null;
  ce_id?: string | null;
  ce_site_id?: string | null;
  unique_claim_id?: string | null;
  claim_status?: string | null;
  claim_type?: string | null;
  encounter_id?: string | null;
  encounter_service_date?: string | null;
  encounter_service_area_code?: string | null;
  location_of_service?: string | null;
  patient_status?: string | null;
  claim_charge_id?: string | null;
  j_code?: string | null;
  charge_code?: string | null;
  diagnosis_code_array?: string[] | null;
  ndc?: string | null;
  drug_name?: string | null;
  compound_indicator?: number | null;
  quantity_dispensed_unit?: number | null;
  quantity_replenished_unit?: number | null;
  medicaid_id?: string | null;
  patient_mrn_number?: string | null;
  patient_first_name?: string | null;
  patient_middle_name?: string | null;
  patient_last_name?: string | null;
  patient_name_prefix?: string | null;
  patient_name_suffix?: string | null;
  patient_dob?: string | null;
  patient_gender?: string | null;
  primary_plan_id?: string | null;
  primary_plan_name?: string | null;
  primary_plan_group?: string | null;
  secondary_plan_id?: string | null;
  secondary_plan_name?: string | null;
  secondary_plan_group?: string | null;
  tertiary_plan_id?: string | null;
  tertiary_plan_name?: string | null;
  tertiary_plan_group?: string | null;
  purchase_account_type?: string | null;
  wholesaler_account_number?: string | null;
  wholesaler_invoice_number?: string | null;
  wholesaler_invoice_date?: string | null;
  ce_340b_identification?: number | null;
  ce_340b_claimed?: number | null;
  rxp_patient_check?: number | null;
  rxp_drug_check?: number | null;
  rxp_payer_check?: number | null;
  rxp_340b_decision?: number | null;
  rxp_claim_certification?: number | null;
  rxp_reason_text?: string | null;
  rxp_codes?: string[] | null;
  rxp_processing_flag?: number | null;
  _row_created_date?: string | null;
  _row_updated_date?: string | null;
}

export type CoveredEntityClaim = CoveredEntityClaimPharmacy &
  CoveredEntityClaimMedical;

export interface CoveredEntityClaimFilters {
  submissionDate?: {
    start?: Date;
    end?: Date;
  };
  serviceDate?: {
    start?: Date;
    end?: Date;
  };
  source?: string;
  category?: ClaimCategory;
  payorType?: CEClaimBusinessType;
  ce340BIdentification?: number;
}

export interface CoveredEntityClaimsGetParams {
  id: string;
  claimCategory: ClaimCategory;
  claimGroup: CEClaimGroup;
  take: number;
  skip: number;
  sort: SortDescriptor[];
  filters: FilterDescriptor[];
}

export interface CoveredEntityClaimsExportParams {
  id: string;
  claimCategory: ClaimCategory;
  claimGroup: CEClaimGroup;
  sort: SortDescriptor[];
  filters: FilterDescriptor[];
}

export function transformCoveredEntityClaim(
  claim: CoveredEntityClaim
): CoveredEntityClaim {
  const dateFields: CoveredEntityClaim = Object.keys(claim)
    .filter((key) => key.includes('date'))
    .reduce((acc: CoveredEntityClaim, val) => {
      const key = val as keyof CoveredEntityClaim;
      try {
        acc[key] =
          claim[key] && (getISODateString(claim[key] as string) as any);
      } catch {
        acc[key] = claim[key] as any;
      }
      return acc;
    }, {});

  return {
    ...claim,
    ...dateFields
  };
}
